import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authService, SignupRequest } from '../services/authService';
import AuthLayout from '../components/public/AuthLayout';
import TextField from '../components/public/TextField';

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<Record<keyof SignupRequest | 'confirmPassword', string>>({
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    first_name: '',
    last_name: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [signupEmail, setSignupEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');

  const update = (key: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setFormData((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (formData.password.length < 14) {
      setError('Password must be at least 14 characters.');
      return;
    }
    setIsLoading(true);
    try {
      const payload: SignupRequest = {
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        first_name: formData.first_name,
        last_name: formData.last_name,
      };
      const response = await authService.signup(payload);
      if (response.needs_verification) {
        setNeedsVerification(true);
        setSignupEmail(formData.email);
      } else {
        navigate('/login');
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } }; message?: string } )
        .response?.data?.detail
        ?? (err as { message?: string }).message
        ?? 'Sign-up failed.';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await authService.verifyPhone({ email: signupEmail, code: verificationCode });
      navigate('/app');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } }; message?: string } )
        .response?.data?.detail
        ?? (err as { message?: string }).message
        ?? 'Verification failed.';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const primaryButton =
    'w-full bg-[var(--primary)] text-white py-3 rounded-lg text-sm font-semibold hover:bg-[var(--primary-light)] disabled:opacity-50 transition-colors shadow-sm';

  if (needsVerification) {
    return (
      <AuthLayout
        title="Verify your phone"
        subtitle={`We sent a 4-6 digit code to the number tied to ${signupEmail}.`}
      >
        <form onSubmit={handleVerify} className="space-y-5">
          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
              {error}
            </div>
          )}
          <TextField
            label="Verification code"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            autoFocus
            required
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
          />
          <button type="submit" disabled={isLoading || !verificationCode} className={primaryButton}>
            {isLoading ? 'Verifying…' : 'Verify and continue'}
          </button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Set up email marketing for your agency in minutes."
      backHref="/"
      backLabel="Back to home"
      footer={
        <Link to="/login" className="block text-[var(--text-muted)] hover:text-[var(--text)]">
          Already have an account? Sign in
        </Link>
      }
    >
      <form onSubmit={handleSignup} className="space-y-5">
        {error && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
            {error}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <TextField label="First name" required value={formData.first_name} onChange={update('first_name')} autoComplete="given-name" />
          <TextField label="Last name" required value={formData.last_name} onChange={update('last_name')} autoComplete="family-name" />
        </div>
        <TextField label="Email" type="email" required value={formData.email} onChange={update('email')} autoComplete="email" />
        <TextField label="Phone" type="tel" required value={formData.phone} onChange={update('phone')} autoComplete="tel" helperText="Used for two-factor verification" />
        <div>
          <div className="flex items-end justify-between mb-1.5">
            <label htmlFor="password" className="block text-sm font-medium text-[var(--text)]">Password</label>
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="text-xs text-[var(--text-muted)] hover:text-[var(--text)]"
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            name="password"
            autoComplete="new-password"
            required
            value={formData.password}
            onChange={update('password')}
            className="w-full px-4 py-2.5 bg-white border border-[var(--border)] rounded-lg text-sm focus:outline-none focus:border-[var(--accent)] focus:ring-4 focus:ring-[var(--accent)]/15 transition-all"
          />
          <p className="mt-1.5 text-xs text-[var(--text-muted)]">At least 14 characters. Avoid common passwords.</p>
        </div>
        <TextField
          label="Confirm password"
          type={showPassword ? 'text' : 'password'}
          autoComplete="new-password"
          required
          value={formData.confirmPassword}
          onChange={update('confirmPassword')}
        />
        <button
          type="submit"
          disabled={isLoading || !formData.email || !formData.password}
          className={primaryButton}
        >
          {isLoading ? 'Creating account…' : 'Create account'}
        </button>
      </form>
    </AuthLayout>
  );
};

export default Signup;
